/** An organisation-wide label tag (#1858). Mirrors barXui.Models.Application.LabelTag. */
export class LabelTag {
  public id: string;
  public name: string;
  public color: string;
  /** Only filled when listing the organisation's tags - number of labels carrying it. */
  public usageCount = 0;

  constructor(tag?: Partial<LabelTag>) {
    if (tag) {
      this.id = tag.id;
      this.name = tag.name;
      this.color = tag.color;
      this.usageCount = tag.usageCount ?? 0;
    }
  }
}

/** Maximum tags per label - enforced by the API too. */
export const MAX_TAGS_PER_LABEL = 5;
export const MAX_TAG_NAME_LENGTH = 50;
export const DEFAULT_TAG_COLOR = '#607d8b';

/** Pick black or white text for a tag colour so the pill stays readable. */
export function tagTextColor(background: string | null | undefined): string {
  const m = /^#?([0-9a-f]{6})$/i.exec((background || DEFAULT_TAG_COLOR).trim());
  if (!m) {
    return '#fff';
  }
  const n = parseInt(m[1], 16);
  const luminance = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return luminance > 0.6 ? '#000' : '#fff';
}

/** Prefix on ids of tags that exist only in the dialog and haven't been created on the server yet. */
export const NEW_TAG_PREFIX = 'new:';

export function isNewTag(tag: LabelTag): boolean {
  return tag.id?.startsWith(NEW_TAG_PREFIX);
}

/**
 * Tag changes made in the label properties dialog (#1858). They are held, not sent, until the label
 * is saved so cancelling the dialog - or never saving the label - leaves nothing behind.
 */
export class TagEdits {
  /** The tags the label should end up with (new ones carry NEW_TAG_PREFIX ids). */
  selected: LabelTag[] = [];
  /** New tags to create (create-or-get by name on the server). */
  created: LabelTag[] = [];
  /** Existing tags renamed / recoloured. */
  updated: LabelTag[] = [];
  /** Existing tags to delete organisation-wide. */
  deletedIds: string[] = [];
  /** True when anything differs from how the label started. */
  changed = false;
}

/**
 * Should editing a label's settings clear its lock? Changing the label itself (name, media, data
 * source...) is deliberate and clears it; a tags-only edit isn't a change to the label, so the lock
 * is left as it was.
 */
export function lockAfterSettingsEdit(settingsChanged: boolean, tagEdits: TagEdits | undefined, currentlyLocked: boolean): boolean {
  if (settingsChanged || !tagEdits?.changed) {
    return false;
  }
  return currentlyLocked;
}

/**
 * Combine tag edits held from an earlier dialog session (not yet saved) with a later one. The later
 * session's edits are relative to the saved tags, so keep any deletes / renames the earlier one
 * queued and let the later selection win.
 */
export function mergeTagEdits(pending: TagEdits | null | undefined, next: TagEdits): TagEdits {
  if (!pending) {
    return next;
  }
  const deleted = new Set([...pending.deletedIds, ...next.deletedIds]);
  const updated = new Map(pending.updated.map(t => [t.id, t]));
  next.updated.forEach(t => updated.set(t.id, t));
  const merged = new TagEdits();
  merged.selected = next.selected;
  merged.created = next.created;
  merged.deletedIds = Array.from(deleted);
  merged.updated = Array.from(updated.values()).filter(t => !deleted.has(t.id));
  merged.changed = true;
  return merged;
}
